/**
 * Android 반응형 스타일 시스템 (태블릿 전용)
 *
 * 디바이스 분류:
 *   - tabletLandscape : 패드 가로 (width ≥ 768px + landscape) — 기본값
 *   - tabletPortrait  : 패드 세로 (width ≥ 768px + portrait)
 *
 * 표준 해상도 기준:
 *   - 패드: Galaxy Tab A8 10.5" (1200×1920dp), iPad 10.2" (810×1080dp)
 */

// ═══════════════════════════════════════════════════════
// 미디어쿼리 상수
// ═══════════════════════════════════════════════════════

/** 패드 분기점 — 너비 (dp) */
export const BREAKPOINT_TABLET = 768

/** MUI useMediaQuery / CSS @media 조건문 */
export const MediaQuery = {
  /** 패드 가로 */
  tabletLandscape: `(min-width: ${BREAKPOINT_TABLET}px) and (orientation: landscape)`,
  /** 패드 세로 */
  tabletPortrait: `(min-width: ${BREAKPOINT_TABLET}px) and (orientation: portrait)`,
  /** orientation 만 */
  portrait: "(orientation: portrait)",
  landscape: "(orientation: landscape)",
} as const

// ═══════════════════════════════════════════════════════
// 디바이스 모드 타입
// ═══════════════════════════════════════════════════════

export type DeviceMode = "tabletLandscape" | "tabletPortrait"

// ═══════════════════════════════════════════════════════
// 헤더 (AppBar / Toolbar)
// ═══════════════════════════════════════════════════════

export const HeaderSize = {
  tabletLandscape: {
    height: 84,
    fontSize: "1.5rem",
    tabFontSize: "1.5rem",
    buttonFontSize: "1.1rem",
    iconSize: 24,
    toolbarPx: 1.25,
    toolbarGap: 2,
  },
  tabletPortrait: {
    height: 84,
    fontSize: "1.3rem",
    tabFontSize: "1.3rem",
    buttonFontSize: "1.0rem",
    iconSize: 22,
    toolbarPx: 1.25,
    toolbarGap: 1.5,
  },
} as const

// ═══════════════════════════════════════════════════════
// 치료실 레이아웃
// ═══════════════════════════════════════════════════════

export const LayoutSize = {
  tabletLandscape: {
    /** Left Panel 너비 (베드 목록) */
    leftPanelWidth: 300,
    /** 패널 간 갭 */
    panelGap: 12,
    /** 루트 패딩 */
    rootPadding: 8,
  },
  tabletPortrait: {
    leftPanelWidth: 264,
    panelGap: 8,
    rootPadding: 8,
  },
} as const

// ═══════════════════════════════════════════════════════
// 베드 카드
// ═══════════════════════════════════════════════════════

export const BedCardSize = {
  tabletLandscape: {
    headerHeight: 48,
    headerPx: 12,
    bedNumberFontSize: "1.0rem",
    patientNameFontSize: "1.1rem",
    bodyPadding: 12,
    treatmentIconMaxWidth: 32,
    treatmentIconFontSize: "0.85rem",
    elapsedFontSize: "1.25rem",
  },
  tabletPortrait: {
    headerHeight: 40,
    headerPx: 10,
    bedNumberFontSize: "0.9rem",
    patientNameFontSize: "1.0rem",
    bodyPadding: 8,
    treatmentIconMaxWidth: 28,
    treatmentIconFontSize: "0.78rem",
    elapsedFontSize: "1.1rem",
  },
} as const

// ═══════════════════════════════════════════════════════
// Full View 그리드 (베드 전체보기)
// ═══════════════════════════════════════════════════════

/** 베드 수에 따른 그리드 열 수 반환 */
export const getFullViewColumns = (bedCount: number, mode: DeviceMode): number => {
  switch (mode) {
    case "tabletLandscape":
      return bedCount <= 4 ? 2 : bedCount <= 9 ? 3 : bedCount <= 16 ? 4 : 5
    case "tabletPortrait":
      return bedCount <= 2 ? 1 : bedCount <= 6 ? 2 : bedCount <= 12 ? 3 : 4
  }
}

/** 베드 수에 따른 그리드 갭 반환 */
export const getFullViewGap = (bedCount: number, mode: DeviceMode): number => {
  switch (mode) {
    case "tabletLandscape":
      return bedCount <= 6 ? 3 : bedCount <= 9 ? 2 : bedCount <= 12 ? 1.5 : 1
    case "tabletPortrait":
      return bedCount <= 6 ? 2 : bedCount <= 9 ? 1.5 : 1
  }
}

// ═══════════════════════════════════════════════════════
// 환자 카드 (대기/치료/완료 패널 목록)
// ═══════════════════════════════════════════════════════

export const PatientCardSize = {
  tabletLandscape: {
    primaryFontSize: "0.95rem",
    secondaryFontSize: "0.8rem",
    tertiaryFontSize: "0.78rem",
    cardPaddingX: 12,
    cardPaddingY: 8,
    sectionHeaderHeight: 32,
    sectionHeaderFontSize: "0.82rem",
  },
  tabletPortrait: {
    primaryFontSize: "0.88rem",
    secondaryFontSize: "0.75rem",
    tertiaryFontSize: "0.72rem",
    cardPaddingX: 10,
    cardPaddingY: 6,
    sectionHeaderHeight: 28,
    sectionHeaderFontSize: "0.78rem",
  },
} as const

// ═══════════════════════════════════════════════════════
// 이미지 섹션
// ═══════════════════════════════════════════════════════

export const ImageSectionSize = {
  tabletLandscape: {
    minHeight: 160,
    buttonFontSize: "1.05rem",
    showButtonText: true,
    thumbnailSize: 80,
  },
  tabletPortrait: {
    minHeight: 120,
    buttonFontSize: "0.95rem",
    showButtonText: false,
    thumbnailSize: 64,
  },
} as const

// ═══════════════════════════════════════════════════════
// 동의서
// ═══════════════════════════════════════════════════════

export const ConsentSize = {
  tabletLandscape: {
    signatureMinHeight: 250,
    completeButtonHeight: 56,
    completeButtonMinWidth: 120,
    completeButtonFontSize: "1rem",
    footerPadding: 12,
    bodyPadding: 12,
  },
  tabletPortrait: {
    signatureMinHeight: 250,
    completeButtonHeight: 56,
    completeButtonMinWidth: 120,
    completeButtonFontSize: "1rem",
    footerPadding: 12,
    bodyPadding: 12,
  },
} as const

// ═══════════════════════════════════════════════════════
// 텍스트 크기 (공통)
// ═══════════════════════════════════════════════════════

export const TextSize = {
  tabletLandscape: {
    /** 제목 (섹션 타이틀) */
    title: "1.05rem",
    /** 본문 */
    body: "0.95rem",
    /** 부가 정보 */
    caption: "0.82rem",
    /** 작은 라벨 */
    label: "0.78rem",
  },
  tabletPortrait: {
    title: "0.95rem",
    body: "0.88rem",
    caption: "0.78rem",
    label: "0.72rem",
  },
} as const

// ═══════════════════════════════════════════════════════
// 아이콘 크기
// ═══════════════════════════════════════════════════════

export const IconSize = {
  tabletLandscape: {
    sm: 18,
    md: 22,
    lg: 28,
  },
  tabletPortrait: {
    sm: 16,
    md: 20,
    lg: 24,
  },
} as const

// ═══════════════════════════════════════════════════════
// 로그인 / 서버 검색 화면
// ═══════════════════════════════════════════════════════

export const LoginSize = {
  tabletLandscape: {
    cardMaxWidth: 480,
    titleFontSize: "2rem",
    inputFontSize: "1.1rem",
    buttonHeight: 56,
    buttonFontSize: "1.1rem",
  },
  tabletPortrait: {
    cardMaxWidth: 400,
    titleFontSize: "1.8rem",
    inputFontSize: "1.0rem",
    buttonHeight: 52,
    buttonFontSize: "1.0rem",
  },
} as const
